import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn';
import type { Density, Orientation } from '../../lib/types';

export type AlertBannerVariant = 'default' | 'info' | 'success' | 'warning' | 'critical';

type AlertBannerDensity = Extract<Density, 'default' | 'compact'>;

type AlertBannerLayout = Orientation;

export interface AlertBannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** @default 'info' */
  variant?: AlertBannerVariant;

  /**
   * `compact` is for dense containers such as cards and drawers: less padding, a
   * smaller icon circle, and square corners so it runs edge to edge.
   * @default 'default'
   */
  density?: AlertBannerDensity;

  /**
   * Where `action` goes: `vertical` below the text, `horizontal` beside it. Use
   * `horizontal` only on a wide surface with short text.
   * @default 'vertical'
   */
  layout?: AlertBannerLayout;

  title?: React.ReactNode;

  children?: React.ReactNode;

  /** Default: an icon per variant. */
  icon?: React.ReactNode;

  /** E.g. a button. */
  action?: React.ReactNode;

  /** Without it, no dismiss button is shown. */
  onDismiss?: () => void;

  /** @default 'Dismiss' */
  dismissLabel?: string;

  className?: string;

  ref?: React.Ref<HTMLDivElement>;
}

const defaultIconComponents = {
  default: Info,
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  critical: AlertCircle,
} satisfies Record<AlertBannerVariant, React.ComponentType<{ className?: string }>>;

const variantStyles = {
  default: 'bg-default-intent-tint text-text-default-intent',
  info: 'bg-info-tint text-text-info',
  success: 'bg-success-tint text-text-success',
  warning: 'bg-warning-tint text-text-warning',
  critical: 'bg-critical-tint text-text-critical',
} satisfies Record<AlertBannerVariant, string>;

const iconCircleStyles = {
  default: 'bg-default-200 text-text-default-intent',
  info: 'bg-info-200 text-text-info',
  success: 'bg-success-200 text-text-success',
  warning: 'bg-warning-200 text-text-warning',
  critical: 'bg-critical-400 text-text-critical',
} satisfies Record<AlertBannerVariant, string>;

export function AlertBanner({
  variant = 'info',
  density = 'default',
  layout = 'vertical',
  title,
  children,
  icon,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
  ref,
  ...props
}: AlertBannerProps) {
  // Only critical interrupts the screen reader.
  const role = variant === 'critical' ? 'alert' : 'status';

  const ariaLive = variant === 'critical' ? 'assertive' : 'polite';

  const compact = density === 'compact';

  return (
    <div
      ref={ref}
      data-variant={variant}
      data-density={density}
      data-layout={layout}
      role={role}
      aria-live={ariaLive}
      className={cn(
        'relative flex w-full items-start',
        compact ? 'gap-2 px-4 py-3 text-sm' : 'gap-3 rounded-lg p-4',
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      <BannerIcon variant={variant} compact={compact} icon={icon} />

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col justify-center',
          compact ? 'min-h-6' : 'min-h-8',
        )}
      >
        {title && <div className="font-medium">{title}</div>}
        {children && (
          <div className={cn(title && (compact ? 'mt-0.5' : 'mt-1'), 'text-sm opacity-90')}>
            {children}
          </div>
        )}
        {action && layout === 'vertical' && (
          <div className={compact ? 'mt-2' : 'mt-3'}>{action}</div>
        )}
      </div>

      {action && layout === 'horizontal' && <div className="shrink-0">{action}</div>}

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            'shrink-0',
            'rounded-md p-1.5',
            '-mr-1.5',
            'focus:outline-2 focus:-outline-offset-2 focus:outline-solid',
            'focus:outline-current/50',
            'opacity-70 hover:opacity-100',
            'transition-opacity',
          )}
          aria-label={dismissLabel}
        >
          <X className="size-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function BannerIcon({
  variant,
  compact,
  icon,
}: {
  variant: AlertBannerVariant;
  compact: boolean;
  icon: React.ReactNode;
}) {
  const DefaultIcon = defaultIconComponents[variant];
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        compact ? 'size-6' : 'size-8',
        iconCircleStyles[variant],
      )}
    >
      {icon ?? (
        <DefaultIcon className={cn('shrink-0', compact ? 'size-4' : 'size-5')} aria-hidden="true" />
      )}
    </div>
  );
}
